import i18n from '@/core/i18n';
import { DEFAULT_CURRENCY_CODE } from '@/modules/common';
import { mapRateSourceFromDTO } from '@/modules/common/mappers';
import { RateMask } from '@/modules/common/utils/rate-mask';
import { RateSource } from '@/modules/projects';
import { TeamMemberRatesOnProjectResponse } from '@/modules/team';

import { RateOption } from '../types';

/**
 * Returns a human-readable label for a rate source.
 * For ProjectBased, returns an empty string — callers should handle this case
 * (e.g., wrapping in "Project based (...)").
 */
export function getRateSourceLabel(rateSource: RateSource): string {
  const rateSourceLabels: Record<RateSource, string> = {
    [RateSource.Custom]: i18n.t('common:rates.custom'),
    [RateSource.FlatRate]: i18n.t('common:rates.projectFlatRate'),
    [RateSource.GlobalDefaultRate]: i18n.t('common:rates.organizationDefaultRate'),
    [RateSource.ProjectRateCard]: i18n.t('common:rates.projectRateCard'),
    [RateSource.RoleBasedRate]: i18n.t('common:rates.roleBasedRate'),
    [RateSource.SeniorityBasedRate]: i18n.t('common:rates.seniorityBasedRate'),
    [RateSource.UserExternalRate]: i18n.t('common:rates.userExternalRate'),
    [RateSource.ProjectBased]: '', // Will be handled specially
  };

  return rateSourceLabels[rateSource] ?? i18n.t('common:rates.custom');
}

/**
 * Builds rate options from API response
 */
export function getRateOptionsFromApi({
  ratesData,
  assignmentOverride,
}: {
  ratesData: TeamMemberRatesOnProjectResponse | undefined;
  /** takes the previous selected option rate for custom option */
  assignmentOverride: number | undefined;
}): RateOption[] {
  if (!ratesData) {
    // Return minimal options when no data available
    return [
      {
        value: RateSource.Custom,
        label: i18n.t('common:rates.custom'),
        disabled: false,
        rate: assignmentOverride,
        currency: DEFAULT_CURRENCY_CODE,
      },
    ];
  }

  const currency = ratesData.currency.code;
  const options: RateOption[] = [];

  // Add "Project based" option first
  const projectRateSource = mapRateSourceFromDTO(ratesData.projectBasedRateOption.source);
  const projectRateSourceLabel = getRateSourceLabel(projectRateSource);

  options.push({
    value: RateSource.ProjectBased,
    label: i18n.t('common:rates.projectBased', { rateSourceName: projectRateSourceLabel }),
    disabled: false,
    rate: ratesData.projectBasedRateOption.rate,
    currency,
  });

  // Add user rate options (deduplicate by rate source value)
  const addedSources = new Set<RateSource>();

  ratesData.rateOptions.forEach(rateOption => {
    const rateSource = mapRateSourceFromDTO(rateOption.source);

    // Skip if we've already added this rate source
    if (addedSources.has(rateSource)) {
      return;
    }
    addedSources.add(rateSource);

    const label = getRateSourceLabel(rateSource);

    options.push({
      value: rateSource,
      label,
      disabled: false,
      rate: rateOption.rate,
      currency,
    });
  });

  // Always add custom option at the end
  options.push({
    value: RateSource.Custom,
    label: i18n.t('common:rates.custom'),
    disabled: false,
    rate: assignmentOverride,
    currency,
  });

  return options;
}

export const renderRateSourceTrigger = option => <div className="truncate">{option?.label}</div>;

export const renderRateSourceOption = (option: RateOption) => (
  <div className="flex w-full justify-between gap-4">
    <span>{option.label}</span>
    <span className="text-sm font-normal text-muted-foreground">
      {option.value === RateSource.Custom
        ? i18n.t('common:rates.manualEntry', {
            rate: RateMask.apply(option?.rate ?? 0),
            currency: option.currency,
          })
        : i18n.t('common:currencyRateWithParenthesis', {
            rate: RateMask.apply(option?.rate ?? 0),
            currency: option.currency,
          })}
    </span>
  </div>
);
